import { useCallback, useEffect, useMemo } from 'react'
import cn from 'classnames'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'

import styles from './Summary.module.scss'

import { APPLICATION_STRINGS } from '../../strings'

import useGetSummaryReport from '../../hooks/useGetSummaryReport'

import { centsToDollars, stringFloatToCents } from '../../utils/formatters'

import SidebarModal from '../SidebarModal'
import { dollarsToCents } from '../../VNUtil/Money'

import {
  getAllRevenueInCents,
  getOrderReportsLastFetchedAt,
  getUnsyncableIds,
  getUnsyncedOrderCounts,
} from '../../selectors/order'

import { getIsSyncing, getOrdersToSyncCount } from '../../VNSyncManager/Selectors'
import { getUseTaxClasses } from '../../selectors/menus'
import Button from '../Button'
import { setShouldStartSync, setShouldStopSync } from '../../VNSyncManager/ActionCreators'
import { setRoute } from '../../VNRoutes/ActionCreators'
import { getVenueName } from '../../selectors/venue'
import { getDeviceId, getDeviceSerial } from '../../utils/deviceData'
import { getSummitMode } from '../../VNMode/Selectors'
import { getTerminalInfo } from '../../selectors/peripheral'
import { getSafResponse } from '../../selectors/appState'
import { getActiveAttendant } from '../../selectors/attendant'

const OrderSummary = ({ className }) => {
  const classNames = cn(styles.orderSummary, className)
  const dispatch = useDispatch()

  // selectors
  const unsyncableIds = useSelector(getUnsyncableIds)
  const allRevenueInCents = useSelector(getAllRevenueInCents)
  const venueName = useSelector(getVenueName)
  const deviceUuid = getDeviceId()
  const deviceSerialNumber = getDeviceSerial()
  const isSummitMode = useSelector(getSummitMode)
  const secondaryDeviceId = useSelector(getTerminalInfo)?.serialNumber ?? ''
  const attendant = useSelector(getActiveAttendant)
  const safResponse = useSelector(getSafResponse)
  const [activeOfflineCardCount, unsyncedLocalOrderCount] = useSelector(getUnsyncedOrderCounts)
  const ordersToSync = useSelector(getOrdersToSyncCount)

  const unsyncableOrders = unsyncableIds.length

  const isSyncing = useSelector(getIsSyncing)
  const orderReportsLastFetchedAt = useSelector(getOrderReportsLastFetchedAt)
  const useTaxClasses = useSelector(getUseTaxClasses)
  // eslint-disable-next-line no-unused-vars
  const [getSummaryReport, { qsTotals: totals, nonQsTotals }] = useGetSummaryReport()

  const urlParams = new URLSearchParams(window.location.search)
  const fromIdleReplay = urlParams.get('from') === 'idleReplay'
  const todaysDate = useMemo(() => moment().format('MM/DD/YY'), [])
  const tomorrowsDate = useMemo(() => moment().add(1, 'days').format('MM/DD/YY'), [])

  const onSyncOrdersClick = useCallback(() => {
    dispatch(setShouldStartSync(fromIdleReplay, null))
  }, [dispatch, fromIdleReplay])

  const onStopSyncClick = useCallback(() => {
    dispatch(setShouldStopSync())
  }, [dispatch])

  useEffect(() => {
    getSummaryReport()

    if (fromIdleReplay) {
      dispatch(setShouldStartSync(true, null))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderFieldRow = (fields, index) => (
    <div className={styles.row} key={`${fields?.columnOne?.value}-${index}`}>
      {fields?.map?.((field, idx) => (
        <div className={styles.column} key={`column_${field}_${idx}`} style={field?.style}>
          {field?.value}
        </div>
      ))}
    </div>
  )

  const createField = (value = '', style = {}) => ({
    value,
    style,
  })

  const addWhiteTextStyle = {
    color: 'white',
  }

  const addBoldWhiteText = {
    color: 'white',
    fontWeight: '700',
  }

  const sectionOneRows = [
    [createField('Venue: ' + venueName)],
    [createField(`Report Date: ${todaysDate}, 3:00AM - ${tomorrowsDate}, 3:00AM`)],
    [createField(`Last Updated: ${orderReportsLastFetchedAt}`)],
  ]

  const getTaxFields = () => {
    if (useTaxClasses) {
      const taxRules = totals?.taxRules ?? {}
      return Object.values(taxRules).map((taxRule) => {
        const taxAmount = centsToDollars(taxRule?.taxAmountInCents)
        return [createField(taxRule.displayName), createField(taxAmount ?? '_', addWhiteTextStyle)]
      })
    }
    return [[createField('Tax'), createField(totals?.tax ?? '_', addWhiteTextStyle)]]
  }

  const taxFields = getTaxFields()

  let sectionTwoRows = [
    [createField(attendant?.name, addBoldWhiteText)],
    [createField('ID: ' + attendant?.uuid, addWhiteTextStyle)],
    [createField('Gross Sales'), createField(totals?.gross ?? centsToDollars(allRevenueInCents), addWhiteTextStyle)],
    [createField('Discounts'), createField(totals?.discounts ?? '-', addWhiteTextStyle)],
    [createField('Refunds'), createField(totals?.refunds ?? '-', addWhiteTextStyle)],
    [createField('Net Sales'), createField(totals?.net ?? centsToDollars(allRevenueInCents), addWhiteTextStyle)],
    ...taxFields,
    [createField('Service Charge'), createField(totals?.serviceCharge ?? '_', addWhiteTextStyle)],
    [createField('Tips'), createField(totals?.tips ?? '-', addWhiteTextStyle)],
  ]

  //add penny rounding if it exists
  const pennyRoundingInCents = stringFloatToCents(totals?.pennyRounding ?? '0.00')

  if (pennyRoundingInCents !== 0) {
    const pennyRoundingField = [createField('Penny Rounding'), createField(totals?.pennyRounding ?? '_', addWhiteTextStyle)]

    sectionTwoRows.push(pennyRoundingField)
  }

  //add Open tabs count
  sectionTwoRows.push([
    createField('Open Tabs', totals?.tabsCount > 0 ? { color: '#EB628D' } : {}),
    createField(totals?.tabsCount ?? '0', { color: totals?.tabsCount > 0 ? '#EB628D' : '#999999' }),
  ])

  const deviceIdentifier = isSummitMode ? `Serial Number: ${deviceSerialNumber}` : `Device ID: ${deviceUuid}`

  const sectionThreeRows = [
    [createField('Device', addBoldWhiteText)],
    [createField(deviceIdentifier, addWhiteTextStyle)],
    [createField('Secondary ID: ' + secondaryDeviceId, addWhiteTextStyle)],
    [
      createField('Orders To Sync', ordersToSync > 0 ? { color: '#EB628D' } : {}),
      createField(ordersToSync, { color: ordersToSync > 0 ? '#EB628D' : 'white' }),
    ],
    [
      createField('Defective Orders Unable to Sync', unsyncableOrders > 0 ? { color: '#EB628D' } : {}),
      createField(unsyncableOrders ?? '0', { color: unsyncableOrders > 0 ? '#EB628D' : '#999999' }),
    ],
    [
      createField('Cash Collected Today', dollarsToCents(totals?.cash) > 0 ? { color: '#EB628D' } : {}),
      createField(totals?.cash ?? '0', { color: dollarsToCents(totals?.cash) > 0 ? '#EB628D' : '#999999' }),
    ],
  ]

  const sections = [sectionOneRows, sectionTwoRows, sectionThreeRows]
  const content = sections?.map?.((section, idx) => {
    return (
      <>
        {section?.map?.(renderFieldRow)}
        <div key={`borderRow-${idx}`} className={styles.borderRow}></div>
      </>
    )
  })

  const onClose = useCallback(() => {
    dispatch(setRoute('/orders'))
  }, [dispatch])

  return (
    <>
      <SidebarModal show title="Sales Report" onClose={onClose}>
        <div className={classNames}>
          <div className={styles.scrollable}>
            {!safResponse?.success && (activeOfflineCardCount > 0 || unsyncedLocalOrderCount > 0) && (
              <div className={styles.syncDetailsContainer}>
                <div>
                  <div>{APPLICATION_STRINGS.SAF_ERROR}</div>
                </div>
              </div>
            )}

            {content}
          </div>

          {!isSyncing ? (
            <div className={styles.syncButtonContainer}>
              <Button useMui className={cn([styles.syncButton, styles.shift4blue])} onClick={onSyncOrdersClick}>
                {APPLICATION_STRINGS.SYNC_ORDERS}
              </Button>
            </div>
          ) : (
            <div className={styles.syncButtonContainer}>
              <Button useMui className={styles.stopSyncButton} onClick={onStopSyncClick}>
                {APPLICATION_STRINGS.STOP_SYNCING}
              </Button>
            </div>
          )}
        </div>
      </SidebarModal>
    </>
  )
}

export default OrderSummary
